import { useState, useEffect, Suspense, useCallback } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "./redux/store";
import App from "./components/App";
import GlobalLoader from "./shared/components/Loader";
import { JobQueueProvider } from "./components/layout/header/JobQueueContext";
import { registerCustomElements } from "./webComponents/registerCustomElements";
import { useMessageBarController } from "./utils/useMessageBarController";
import { hydrateFromSession } from "./utils/useSessionHydration";
import { createLogoutHandler } from "./utils/useLogout";
import { getTenantConfig } from "./utils/getTenantConfig";
import { useFavicon } from "./utils/useFavicon";
import { safeParse } from "./helper/helper";
import { lazyWithRetry } from "./shared/services/shared";
// OFFLINE-UX — one line. DU.md §33 group C.
import OfflineGuard from "./shared/components/OfflineGuard";
import { setupLiveRegions } from "./utils/liveAnnouncer";
import { useSessionExpiry } from "./utils/useSessionExpiry";
import SessionExpiryModal from "./shared/components/SessionExpiryModal";
import "./styles/index.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import * as serviceWorker from "./serviceWorker";
import "./components/simlify/Zendesk.css";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./styles/theme";
import { CssBaseline } from "@mui/material";

const LogoutStyle = {
  awsloader: {
    position: "absolute",
    margin: "auto",
    width: "20%",
    height: "20%",
    top: "0",
    left: "0",
    bottom: "0",
    right: "0",
    fontSize: "20px",
    fontWeight: "bold",
  },
};
const MessageBar = lazyWithRetry(() => import("./shared/components/MessageBar"));

registerCustomElements();
// The screen reader's announcement regions have to be in the page BEFORE the
// first message, or there is no change for it to announce. Plain DOM nodes, no
// styles and nothing rendered, so this cannot affect the stylesheet or the UI.
setupLiveRegions();

const Root = () => {
  const dispatch = useDispatch();
  const { messageBar, menus, isLoading } = useSelector((s) => s);
  const { user = {}, loggedIn = {} } = useSelector((s) => s.user || {});

  const [state, setState] = useState({
    ...hydrateFromSession(),
    isLoading: false,
    fullPageLoading: false,
    isSFTicketHolded: false,
    awsLogoutLoading: false,
    openCreateTicketBox: false,
    tenantConfig: null,
    hosttoken: null,
    serviceNowTicketHold: null,
  });

  const { showMessage, hideMessage } = useMessageBarController();

  useFavicon(state.tenantConfig);

  const logoutUser = createLogoutHandler(dispatch, setState);

  const addToState = (key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // preserve exactly the helper behavior
  const isSFHoldedTicket = useCallback(() => {
    const ticketInfo = safeParse(sessionStorage.getItem("holdSFTicket"));
    try {
      const parsed = ticketInfo ? ticketInfo : null;
      return !!(parsed && parsed.isAllowHold);
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    getTenantConfig({ dispatch, setState, showMessage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session expiry management
  const { showSessionModal, timeRemaining, handleStaySignedIn, handleLogoutClick, handleNotNow } =
    useSessionExpiry({
      isLoggedIn: user?.isLoggedIn,
      logoutUser,
    });

  if (state.awsLogoutLoading)
    return (
      <div style={LogoutStyle.awsloader}>
        <p>Logging out....</p>
      </div>
    );
  if (state.fullPageLoading) return <GlobalLoader />;

  return (
    <>
      {/* OFFLINE-UX — one line. DU.md §33 group C. */}
      <OfflineGuard />
      <Suspense fallback={null}>
        {messageBar?.open && <MessageBar disableMessage={hideMessage} />}
      </Suspense>
      <SessionExpiryModal
        open={showSessionModal}
        onStaySignedIn={handleStaySignedIn}
        onLogout={handleLogoutClick}
        onNotNow={handleNotNow}
        timeRemaining={timeRemaining}
      />
      <JobQueueProvider>
        <BrowserRouter
          {...state}
          user={user}
          loggedIn={loggedIn}
          menus={menus?.menus || []}
          addToState={addToState}
          logoutUser={logoutUser}
          showMessageBar={showMessage}
          isSFHoldedTicket={isSFHoldedTicket}
          disableMessage={hideMessage}
        >
          <HelmetProvider>
            {(state?.isLoading || isLoading) && <GlobalLoader />}
            <App
              {...state}
              user={user}
              loggedIn={loggedIn}
              menus={menus?.menus || []}
              addToState={addToState}
              isSFHoldedTicket={isSFHoldedTicket}
              logoutUser={logoutUser}
              showMessageBar={showMessage}
              disableMessage={hideMessage}
            />
          </HelmetProvider>
        </BrowserRouter>
      </JobQueueProvider>
    </>
  );
};

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container);
root.render(
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Root />
    </ThemeProvider>
  </Provider>
);

// service worker left as original
serviceWorker.unregister();

export default Root;
